import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { ListSelector } from "../components/ListSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { ArrowLeft, Loader2, Radio, Save, Check, ChevronsUpDown, ChevronDown, X, Search } from "lucide-react";
import { toast } from "sonner";

const CONNECTION_TYPES = ["RTB", "VAST", "Prebid", "JS"];
const OPENRTB_VERSIONS = ["2.5", "2.6"];
const RTB_CONNECTION_OPTIONS = ["RTB_TO_ALL", "RTB_TO_RTB", "RTB_TO_VAST", "RTB_TO_JS"];
const AD_FORMATS = ["Banner", "Native", "Video", "Rewarded_video", "Audio"];
const TRAFFIC_TYPES = ["Mobile_web", "Mobile_app", "Desktop", "CTV"];
const MARGIN_TYPES = ["Fixed", "Adaptive", "Range"];
const SCHAIN_TYPES = ["SSP_Schain", "Platform_Schain", "SSP_Source_Schain", "DSP_Source_Schain", "Complete_Schain"];
const SECURE_MODES = ["Both", "Secure_only", "Non_secure_only"];

// Country regions for bulk selection
const COUNTRY_REGIONS = {
  "North America": ["US", "CA", "MX"],
  "Europe (EU)": ["AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE"],
  "Europe (Non-EU)": ["GB", "CH", "NO", "IS", "AL", "AD", "BA", "BY", "MD", "MC", "ME", "MK", "RS", "SM", "UA", "VA", "RU"],
  "Asia Pacific (APAC)": ["AU", "NZ", "JP", "KR", "CN", "HK", "TW", "SG", "MY", "TH", "ID", "PH", "VN", "IN", "BD", "PK", "LK", "NP"],
  "Middle East": ["AE", "SA", "QA", "KW", "BH", "OM", "IL", "JO", "LB", "IQ", "IR", "TR", "EG"],
  "Latin America (LATAM)": ["BR", "AR", "CL", "CO", "PE", "VE", "EC", "BO", "PY", "UY", "CR", "PA", "GT", "HN", "SV", "NI", "CU", "DO", "PR"],
  "Africa": ["ZA", "NG", "KE", "EG", "MA", "GH", "TZ", "ET", "UG", "DZ", "TN", "SN", "CI", "CM", "AO", "MZ", "ZW", "ZM", "BW", "NA", "RW"],
};

const REGION_NAMES = Object.keys(COUNTRY_REGIONS);

// Ad size definitions by format category
const BANNER_SIZES = [
  "320x50", "320x480", "300x250", "728x90", "160x600", "336x280",
  "300x600", "970x250", "970x90", "468x60", "120x600", "250x250",
  "200x200", "320x100", "300x100", "300x50", "480x320", "88x31",
  "120x60", "234x60", "180x150", "125x125", "120x240", "240x400",
];
const VIDEO_SIZES = [
  "640x480", "640x360", "854x480", "1280x720", "1920x1080",
  "320x480", "480x320", "768x1024", "1024x768", "320x568",
  "375x667", "414x736", "360x640", "412x732",
];

function AdSizeSelector({ selectedSizes, onChange, adFormats }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [customValue, setCustomValue] = useState("");

  const hasBanner = adFormats.some(f => ["Banner", "Native"].includes(f));
  const hasVideo = adFormats.some(f => ["Video", "Rewarded_video", "Audio"].includes(f));
  const hasAll = hasBanner && hasVideo;

  const availableSizes = useMemo(() => {
    let sizes = [];
    if (hasAll || (!hasBanner && !hasVideo)) {
      sizes = [...new Set([...BANNER_SIZES, ...VIDEO_SIZES])];
    } else if (hasBanner) {
      sizes = [...BANNER_SIZES];
    } else if (hasVideo) {
      sizes = [...VIDEO_SIZES];
    }
    // Add any selected sizes that aren't in the standard lists (custom)
    for (const s of selectedSizes) {
      if (!sizes.includes(s)) sizes.push(s);
    }
    return sizes;
  }, [hasBanner, hasVideo, hasAll, selectedSizes]);

  const filteredSizes = useMemo(() => {
    if (!search) return availableSizes;
    return availableSizes.filter(s => s.includes(search));
  }, [availableSizes, search]);

  const selectedSet = useMemo(() => new Set(selectedSizes), [selectedSizes]);

  const toggleSize = (size) => {
    if (selectedSet.has(size)) {
      onChange(selectedSizes.filter(s => s !== size));
    } else {
      onChange([...selectedSizes, size]);
    }
  };

  const addCustom = () => {
    const val = customValue.trim();
    if (/^\d+x\d+$/.test(val) && !selectedSet.has(val)) {
      onChange([...selectedSizes, val]);
      setCustomValue("");
    }
  };

  const selectAll = () => onChange([...new Set([...selectedSizes, ...filteredSizes])]);
  const deselectAll = () => onChange([]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" aria-expanded={open} className="w-[280px] justify-between" data-testid="ad-size-select">
              {selectedSizes.length === 0 ? "Select sizes..." : `${selectedSizes.length} sizes selected`}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search sizes..." value={search} onValueChange={setSearch} />
              <div className="flex gap-1 px-2 py-1 border-b">
                <Button type="button" variant="ghost" size="sm" className="h-6 text-[10px]" onClick={selectAll}>Select All</Button>
                <Button type="button" variant="ghost" size="sm" className="h-6 text-[10px]" onClick={deselectAll}>Deselect All</Button>
              </div>
              <CommandList>
                <CommandEmpty>No size found.</CommandEmpty>
                <CommandGroup>
                  {filteredSizes.map((size) => (
                    <CommandItem key={size} value={size} onSelect={() => toggleSize(size)}>
                      <Check className={`mr-2 h-4 w-4 ${selectedSet.has(size) ? "opacity-100" : "opacity-0"}`} />
                      <span className="font-mono text-sm">{size}</span>
                      {BANNER_SIZES.includes(size) && !VIDEO_SIZES.includes(size) && <span className="ml-auto text-[10px] text-muted-foreground">Banner</span>}
                      {VIDEO_SIZES.includes(size) && !BANNER_SIZES.includes(size) && <span className="ml-auto text-[10px] text-muted-foreground">Video</span>}
                      {!BANNER_SIZES.includes(size) && !VIDEO_SIZES.includes(size) && <span className="ml-auto text-[10px] text-muted-foreground">Custom</span>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Input
          placeholder="Custom e.g. 640x480"
          className="w-[160px]"
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); addCustom(); } }}
          data-testid="custom-banner-size-input"
        />
        <Button type="button" variant="outline" size="sm" onClick={addCustom} data-testid="add-custom-size-btn">Add</Button>
      </div>

      {selectedSizes.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selectedSizes.map((size) => (
            <Badge key={size} variant="secondary" className="text-xs px-2 py-0.5 cursor-pointer hover:bg-destructive/20" onClick={() => toggleSize(size)}>
              {size} ×
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No sizes selected — all sizes are accepted</p>
      )}
    </div>
  );
}

// Country Multi-Select Dropdown Component - No flickering
function CountrySelector({ countries, selectedCountries, onBulkSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  
  // Memoize selected set for O(1) lookups
  const selectedSet = useMemo(() => new Set(selectedCountries), [selectedCountries]);
  
  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!search) return countries;
    const query = search.toLowerCase();
    return countries.filter(c => 
      c.name.toLowerCase().includes(query) || c.code.toLowerCase().includes(query)
    );
  }, [countries, search]);

  // Memoize region codes map
  const regionCodesMap = useMemo(() => {
    const countryCodes = new Set(countries.map(c => c.code));
    const map = {};
    REGION_NAMES.forEach(name => {
      map[name] = COUNTRY_REGIONS[name].filter(code => countryCodes.has(code));
    });
    return map;
  }, [countries]);

  // Calculate region stats
  const regionStats = useMemo(() => {
    const stats = {};
    REGION_NAMES.forEach(name => {
      const codes = regionCodesMap[name];
      const selectedCount = codes.filter(c => selectedSet.has(c)).length;
      stats[name] = {
        total: codes.length,
        selected: selectedCount,
        allSelected: selectedCount === codes.length && codes.length > 0
      };
    });
    return stats;
  }, [regionCodesMap, selectedSet]);

  // Handlers
  const handleToggleCountry = useCallback((code) => {
    const newSet = new Set(selectedCountries);
    if (newSet.has(code)) {
      newSet.delete(code);
    } else {
      newSet.add(code);
    }
    onBulkSelect(Array.from(newSet));
  }, [selectedCountries, onBulkSelect]);

  const handleRegionClick = useCallback((regionName) => {
    const codes = regionCodesMap[regionName];
    const stats = regionStats[regionName];
    let newSelection;
    if (stats.allSelected) {
      const codesToRemove = new Set(codes);
      newSelection = selectedCountries.filter(c => !codesToRemove.has(c));
    } else {
      const newSet = new Set(selectedCountries);
      codes.forEach(code => newSet.add(code));
      newSelection = Array.from(newSet);
    }
    onBulkSelect(newSelection);
  }, [regionCodesMap, regionStats, selectedCountries, onBulkSelect]);

  const handleSelectAll = useCallback(() => {
    onBulkSelect(countries.map(c => c.code));
  }, [countries, onBulkSelect]);

  const handleDeselectAll = useCallback(() => {
    onBulkSelect([]);
  }, [onBulkSelect]);

  const removeCountry = useCallback((code) => {
    onBulkSelect(selectedCountries.filter(c => c !== code));
  }, [selectedCountries, onBulkSelect]);

  return (
    <div className="space-y-3">
      {/* Region Quick Select Buttons */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Quick Select by Region</Label>
        <div className="flex flex-wrap gap-2">
          {REGION_NAMES.map((regionName) => {
            const stats = regionStats[regionName];
            return (
              <Button
                key={regionName}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleRegionClick(regionName)}
                className={`text-xs ${
                  stats.allSelected 
                    ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                    : stats.selected > 0
                      ? "bg-secondary" 
                      : ""
                }`}
              >
                {regionName} ({stats.selected}/{stats.total})
              </Button>
            );
          })}
        </div>
      </div>

      {/* Bulk Actions */}
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={handleSelectAll}>
          Select All ({countries.length})
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={handleDeselectAll}>
          Deselect All
        </Button>
      </div>

      {/* Country Dropdown */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Select Countries</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between h-auto min-h-10 py-2"
            >
              <span className="text-left truncate">
                {selectedCountries.length === 0 
                  ? "Select countries..." 
                  : `${selectedCountries.length} countries selected`}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput 
                placeholder="Search countries..." 
                value={search}
                onValueChange={setSearch}
              />
              <CommandList className="max-h-64">
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {filteredCountries.map((country) => {
                    const isSelected = selectedSet.has(country.code);
                    return (
                      <CommandItem
                        key={country.code}
                        value={country.code}
                        onSelect={() => handleToggleCountry(country.code)}
                        className="cursor-pointer"
                      >
                        <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                          isSelected ? "bg-primary border-primary" : "border-muted"
                        }`}>
                          {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <span className="font-medium mr-1">{country.code}</span>
                        <span className="text-muted-foreground">{country.name}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Selected Countries Display */}
      {selectedCountries.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Selected ({selectedCountries.length})</Label>
          <div className="flex flex-wrap gap-1 p-2 border rounded-lg bg-muted/30 max-h-24 overflow-y-auto">
            {selectedCountries.map((code) => {
              const country = countries.find(c => c.code === code);
              return (
                <Badge key={code} variant="secondary" className="gap-1 text-xs">
                  {code} {country ? `- ${country.name}` : ''}
                  <X 
                    className="h-3 w-3 cursor-pointer hover:text-destructive" 
                    onClick={() => removeCountry(code)} 
                  />
                </Badge>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DSPFormPage() {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [ssps, setSsps] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [deviceOSList, setDeviceOSList] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    connection_type: "RTB",
    openrtb_version: "2.5",
    qps_adapter: 100,
    min_rcpm: 0,
    tmax: 0,
    rtb_connection: "RTB_TO_ALL",
    ad_formats: ["Banner"],
    traffic_types: ["Mobile_web"],
    company_id: "",
    region: "US_EAST",
    margin_type: "Fixed",
    margin: 0,
    custom_adapter: "",
    custom_adapter_enabled: false,
    adapter_config: {
      adapter_type: "none",
      account_id: "",
      site_id: "",
      zone_id: "",
      size_id: "",
      pos: "",
    },
    endpoint_url: "",
    call_dsp_nurl: false,
    stats_api_link: "",
    schain_type: "SSP_Schain",
    process_dual_schain: false,
    schain_modification: "none",
    schain_complete_status: "none",
    schain_mode_limit: "none",
    filters: {
      countries_mode: "Allow",
      countries: [],
      ssps_mode: "Allow",
      ssps: [],
      device_os_mode: "Allow",
      device_os: [],
      filter_lists: [],
      min_bidfloor: 0,
      max_bidfloor: "",
      banner_sizes_mode: "Allow",
      banner_sizes: [],
    },
    advanced_filters: {
      mismatched_ip_filter: false,
      mismatched_bundle_filter: false,
      iab_ott_ctv_compliance: false,
      tcf: false,
      ccpa_compliance: false,
      only_with_ifa: false,
      placement_required: false,
      ipv4_required: false,
      badv_limit: "",
      iab_subcategories_limit: "",
      only_with_app_name: false,
      exclude_mraid_responses: false,
      add_mraid_impression_pixel: false,
      coppa_filtration: false,
      lmt_dnt_filter: false,
      secure_mode: "Both",
      connection_type: "RTB",
      cellular_connection_enabled: false,
      cellular_connection_type: "All",
      adult_filter: "allow_all",
    },
    settings: {
      pass_omsdk: false,
      pass_display_manager: false,
      pass_pchain: false,
      send_gzip_requests: false,
    },
  });

  // Raw string state for numeric inputs - prevents React from eating decimals/zeros
  const [rawInputs, setRawInputs] = useState({
    qps_adapter: "100",
    min_rcpm: "0",
    tmax: "0",
    margin: "0",
    min_bidfloor: "0",
    max_bidfloor: "",
  });

  const updateRawInput = (field, value) => {
    setRawInputs((prev) => ({ ...prev, [field]: value }));
  };

  const commitRawInput = (field, isInteger) => {
    const raw = rawInputs[field];
    if (raw === "" || raw === undefined) {
      updateField(field, isInteger ? 0 : 0);
      return;
    }
    const parsed = isInteger ? parseInt(raw, 10) : parseFloat(raw);
    if (!isNaN(parsed)) {
      updateField(field, parsed);
    }
  };

  useEffect(() => {
    fetchReferenceData();
    if (isEdit) {
      fetchDSP();
    }
  }, [id]);

  const fetchReferenceData = async () => {
    try {
      const [companiesRes, sspsRes, regionsRes, countriesRes, deviceOSRes] = await Promise.all([
        api.get("/companies"),
        api.get("/ssps"),
        api.get("/reference/regions"),
        api.get("/reference/countries"),
        api.get("/reference/device-os"),
      ]);
      setCompanies(companiesRes.data);
      setSsps(sspsRes.data);
      setRegions(regionsRes.data);
      setCountries(countriesRes.data);
      setDeviceOSList(deviceOSRes.data);
    } catch (error) {
      toast.error("Failed to load reference data");
    }
  };

  const fetchDSP = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/dsps/${id}`);
      const data = response.data;
      // Merge server data with defaults so missing fields don't crash
      setFormData((prev) => ({
        ...prev,
        ...data,
        ad_formats: data.ad_formats || prev.ad_formats,
        traffic_types: data.traffic_types || prev.traffic_types,
        region: data.region || prev.region,
        connection_type: data.connection_type || prev.connection_type,
        openrtb_version: data.openrtb_version || data.ortb_version || prev.openrtb_version,
        margin_type: data.margin_type || prev.margin_type,
        margin: data.margin ?? prev.margin,
        min_rcpm: data.min_rcpm ?? data.rcpm ?? prev.min_rcpm,
        custom_adapter_enabled: data.custom_adapter_enabled ?? prev.custom_adapter_enabled,
        rtb_connection: data.rtb_connection || prev.rtb_connection,
        schain_type: data.schain_type || prev.schain_type,
        schain_modification: data.schain_modification || prev.schain_modification,
        schain_complete_status: data.schain_complete_status || prev.schain_complete_status,
        schain_mode_limit: data.schain_mode_limit || prev.schain_mode_limit,
        filters: {
          ...prev.filters,
          ...(data.filters || {}),
          countries: data.filters?.countries || prev.filters.countries,
          ssps: data.filters?.ssps || prev.filters.ssps,
          device_os: data.filters?.device_os || prev.filters.device_os,
          min_bidfloor: data.filters?.min_bidfloor ?? prev.filters.min_bidfloor,
          max_bidfloor: data.filters?.max_bidfloor ?? prev.filters.max_bidfloor,
          banner_sizes_mode: data.filters?.banner_sizes_mode || prev.filters.banner_sizes_mode,
          banner_sizes: data.filters?.banner_sizes || prev.filters.banner_sizes,
        },
        advanced_filters: {
          ...prev.advanced_filters,
          ...(data.advanced_filters || {}),
        },
        settings: {
          ...prev.settings,
          ...(data.settings || {}),
        },
        adapter_config: {
          ...prev.adapter_config,
          ...(data.adapter_config || {}),
        },
      }));
      setRawInputs({
        qps_adapter: String(data.qps_adapter ?? "100"),
        min_rcpm: String(data.min_rcpm ?? data.rcpm ?? "0"),
        tmax: String(data.tmax ?? "0"),
        margin: String(data.margin ?? "0"),
        min_bidfloor: String(data.filters?.min_bidfloor ?? "0"),
        max_bidfloor: String(data.filters?.max_bidfloor ?? ""),
      });
    } catch (error) {
      toast.error("Failed to fetch DSP");
      navigate("/dsps");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Commit raw inputs to submitData
    const submitData = { ...formData };
    submitData.qps_adapter = rawInputs.qps_adapter === "" ? 100 : parseInt(rawInputs.qps_adapter, 10) || 100;
    submitData.min_rcpm = rawInputs.min_rcpm === "" ? 0 : parseFloat(rawInputs.min_rcpm) || 0;
    submitData.tmax = rawInputs.tmax === "" ? 0 : parseInt(rawInputs.tmax, 10) || 0;
    submitData.margin = rawInputs.margin === "" ? 0 : parseFloat(rawInputs.margin) || 0;
    submitData.filters = {
      ...submitData.filters,
      min_bidfloor: rawInputs.min_bidfloor === "" ? 0 : parseFloat(rawInputs.min_bidfloor) || 0,
      max_bidfloor: rawInputs.max_bidfloor === "" ? null : parseFloat(rawInputs.max_bidfloor) || 0,
    };

    if (!submitData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!submitData.company_id) {
      toast.error("Company is required");
      return;
    }
    if (!submitData.endpoint_url.trim()) {
      toast.error("Endpoint URL is required");
      return;
    }
    if (submitData.filters.max_bidfloor === null || submitData.filters.max_bidfloor === undefined) {
      toast.error("Max Bidfloor is mandatory");
      return;
    }
    if (submitData.filters.min_bidfloor > submitData.filters.max_bidfloor) {
      toast.error("Min Bidfloor cannot be greater than Max Bidfloor");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/dsps/${id}`, submitData);
        toast.success("DSP updated successfully");
      } else {
        await api.post("/dsps", submitData);
        toast.success("DSP created successfully");
      }
      navigate("/dsps");
    } catch (error) {
      // Handle Pydantic validation errors (array of objects) or simple string errors
      const detail = error.response?.data?.detail;
      let errorMsg = "Failed to save DSP";
      if (typeof detail === "string") {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateFilter = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      filters: { ...prev.filters, [field]: value },
    }));
  };

  const updateAdvancedFilter = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      advanced_filters: { ...prev.advanced_filters, [field]: value },
    }));
  };

  const updateSetting = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [field]: value },
    }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData((prev) => {
      const arr = prev[field];
      const newArr = arr.includes(item)
        ? arr.filter((i) => i !== item)
        : [...arr, item];
      return { ...prev, [field]: newArr };
    });
  };

  const toggleFilterArrayItem = (field, item) => {
    setFormData((prev) => {
      const arr = prev.filters[field];
      const newArr = arr.includes(item)
        ? arr.filter((i) => i !== item)
        : [...arr, item];
      return {
        ...prev,
        filters: { ...prev.filters, [field]: newArr },
      };
    });
  };

  // Memoized handlers for CountrySelector
  const handleCountryToggle = useCallback((code) => {
    setFormData((prev) => {
      const arr = prev.filters.countries;
      const newArr = arr.includes(code)
        ? arr.filter((c) => c !== code)
        : [...arr, code];
      return {
        ...prev,
        filters: { ...prev.filters, countries: newArr },
      };
    });
  }, []);

  const handleCountryBulkSelect = useCallback((codes) => {
    setFormData((prev) => ({
      ...prev,
      filters: { ...prev.filters, countries: codes },
    }));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dsp-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dsps")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Radio className="h-8 w-8 text-accent" />
            {isEdit ? "Edit DSP" : "Create DSP"}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? "Update DSP endpoint configuration" : "Configure a new Demand-Side Platform endpoint"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-2xl">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="targeting">Targeting</TabsTrigger>
            <TabsTrigger value="filters">Filters</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>Configure the basic DSP endpoint settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => updateField("name", e.target.value)}
                      placeholder="Enter DSP name"
                      data-testid="dsp-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Company *</Label>
                    <Select
                      value={formData.company_id}
                      onValueChange={(v) => updateField("company_id", v)}
                    >
                      <SelectTrigger data-testid="company-select">
                        <SelectValue placeholder="Select company" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="connection_type">Connection Type</Label>
                    <Select
                      value={formData.connection_type}
                      onValueChange={(v) => updateField("connection_type", v)}
                    >
                      <SelectTrigger data-testid="connection-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONNECTION_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="openrtb_version">OpenRTB Version</Label>
                    <Select
                      value={formData.openrtb_version}
                      onValueChange={(v) => updateField("openrtb_version", v)}
                    >
                      <SelectTrigger data-testid="openrtb-version-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OPENRTB_VERSIONS.map((version) => (
                          <SelectItem key={version} value={version}>
                            v{version}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="region">Region *</Label>
                    <Select
                      value={formData.region}
                      onValueChange={(v) => updateField("region", v)}
                    >
                      <SelectTrigger data-testid="region-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="rtb_connection">RTB Connection</Label>
                    <Select
                      value={formData.rtb_connection}
                      onValueChange={(v) => updateField("rtb_connection", v)}
                    >
                      <SelectTrigger data-testid="rtb-connection-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RTB_CONNECTION_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {opt.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-4 pt-6">
                <div className="flex items-center justify-between p-3 border rounded-lg mb-4">
                  <div>
                    <Label>Custom Adapter</Label>
                    <p className="text-sm text-muted-foreground">Enable to configure Min RCPM</p>
                  </div>
                  <Switch
                    checked={formData.custom_adapter_enabled || false}
                    onCheckedChange={(v) => updateField("custom_adapter_enabled", v)}
                    data-testid="custom-adapter-switch"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="qps_adapter">QPS Limit</Label>
                    <Input
                      id="qps_adapter"
                      type="text"
                      inputMode="numeric"
                      value={rawInputs.qps_adapter}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*$/.test(val)) {
                          updateRawInput("qps_adapter", val);
                        }
                      }}
                      onBlur={() => commitRawInput("qps_adapter", true)}
                      placeholder="100"
                      data-testid="qps-limit-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tmax">TMAX (ms)</Label>
                    <Input
                      id="tmax"
                      type="text"
                      inputMode="numeric"
                      value={rawInputs.tmax}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*$/.test(val)) {
                          updateRawInput("tmax", val);
                        }
                      }}
                      onBlur={() => commitRawInput("tmax", true)}
                      placeholder="0 = use SSP tmax"
                      data-testid="dsp-tmax-input"
                    />
                    <p className="text-[10px] text-muted-foreground">0 = use SSP tmax. Set per-DSP timeout in milliseconds.</p>
                  </div>

                  {formData.custom_adapter_enabled && (
                    <div className="space-y-2">
                      <Label htmlFor="min_rcpm">Min RCPM</Label>
                      <Input
                        id="min_rcpm"
                        type="text"
                        inputMode="decimal"
                        value={rawInputs.min_rcpm}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^\d*\.?\d*$/.test(val)) {
                            updateRawInput("min_rcpm", val);
                          }
                        }}
                        onBlur={() => commitRawInput("min_rcpm", false)}
                        placeholder="0.00"
                        data-testid="min-rcpm-input"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="margin_type">Margin Type</Label>
                    <Select
                      value={formData.margin_type}
                      onValueChange={(v) => updateField("margin_type", v)}
                    >
                      <SelectTrigger data-testid="margin-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MARGIN_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="margin">Margin (%)</Label>
                    <Input
                      id="margin"
                      type="text"
                      inputMode="decimal"
                      value={rawInputs.margin}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*\.?\d*$/.test(val)) {
                          updateRawInput("margin", val);
                        }
                      }}
                      onBlur={() => commitRawInput("margin", false)}
                      placeholder="0.0"
                      data-testid="margin-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="min_bidfloor">Min Bidfloor (CPM)</Label>
                    <Input
                      id="min_bidfloor"
                      type="text"
                      inputMode="decimal"
                      value={rawInputs.min_bidfloor}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*\.?\d*$/.test(val)) {
                          updateRawInput("min_bidfloor", val);
                        }
                      }}
                      onBlur={() => {
                        const raw = rawInputs.min_bidfloor;
                        const parsed = raw === "" ? 0 : parseFloat(raw) || 0;
                        setFormData(prev => ({...prev, filters: {...prev.filters, min_bidfloor: parsed}}));
                      }}
                      placeholder="0.00 (optional)"
                      data-testid="min-bidfloor-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="max_bidfloor">Max Bidfloor (CPM) *</Label>
                    <Input
                      id="max_bidfloor"
                      type="text"
                      inputMode="decimal"
                      value={rawInputs.max_bidfloor}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*\.?\d*$/.test(val)) {
                          updateRawInput("max_bidfloor", val);
                        }
                      }}
                      onBlur={() => {
                        const raw = rawInputs.max_bidfloor;
                        if (raw !== "") {
                          const parsed = parseFloat(raw) || 0;
                          setFormData(prev => ({...prev, filters: {...prev.filters, max_bidfloor: parsed}}));
                        }
                      }}
                      placeholder="Required"
                      data-testid="max-bidfloor-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Endpoint & API</CardTitle>
                <CardDescription>Configure adapter, endpoint URL and statistics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Custom Adapter Selection */}
                <div className="space-y-2">
                  <Label>Custom Adapter</Label>
                  <Select
                    value={formData.adapter_config?.adapter_type || "none"}
                    onValueChange={(v) => updateField("adapter_config", { ...formData.adapter_config, adapter_type: v })}
                  >
                    <SelectTrigger data-testid="adapter-type-select">
                      <SelectValue placeholder="Select adapter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="rubicon">Rubicon</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Rubicon Adapter Fields */}
                {formData.adapter_config?.adapter_type === "rubicon" && (
                  <div className="space-y-3 p-4 border rounded-lg border-blue-200 dark:border-blue-900 bg-blue-50/50 dark:bg-blue-950/20">
                    <p className="text-sm font-semibold text-blue-600">Additional Adapter Fields</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">account_id *</Label>
                        <Input
                          value={formData.adapter_config?.account_id || ""}
                          onChange={(e) => updateField("adapter_config", { ...formData.adapter_config, account_id: e.target.value })}
                          placeholder="Rubicon account ID"
                          data-testid="rubicon-account-id"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">site_id *</Label>
                        <Input
                          value={formData.adapter_config?.site_id || ""}
                          onChange={(e) => updateField("adapter_config", { ...formData.adapter_config, site_id: e.target.value })}
                          placeholder="Rubicon site ID"
                          data-testid="rubicon-site-id"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">zone_id *</Label>
                        <Input
                          value={formData.adapter_config?.zone_id || ""}
                          onChange={(e) => updateField("adapter_config", { ...formData.adapter_config, zone_id: e.target.value })}
                          placeholder="Rubicon zone ID"
                          data-testid="rubicon-zone-id"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">size_id</Label>
                        <Input
                          value={formData.adapter_config?.size_id || ""}
                          onChange={(e) => updateField("adapter_config", { ...formData.adapter_config, size_id: e.target.value })}
                          placeholder="Optional"
                          data-testid="rubicon-size-id"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">pos</Label>
                        <Input
                          value={formData.adapter_config?.pos || ""}
                          onChange={(e) => updateField("adapter_config", { ...formData.adapter_config, pos: e.target.value })}
                          placeholder="Optional"
                          data-testid="rubicon-pos"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Endpoint URL */}
                <div className="space-y-2">
                  <Label htmlFor="endpoint_url">Endpoint URL *</Label>
                  <Input
                    id="endpoint_url"
                    value={formData.endpoint_url}
                    onChange={(e) => updateField("endpoint_url", e.target.value)}
                    placeholder="https://dsp.example.com/bid"
                    data-testid="endpoint-url-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Call DSP NURL</Label>
                  <Select
                    value={formData.call_dsp_nurl === true ? "on_win" : (formData.call_dsp_nurl || "disabled")}
                    onValueChange={(v) => updateField("call_dsp_nurl", v)}
                  >
                    <SelectTrigger data-testid="call-dsp-nurl-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="disabled">Disabled</SelectItem>
                      <SelectItem value="on_win">On Win</SelectItem>
                      <SelectItem value="on_impression">On Impression</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">On Win: fire NURL when DSP wins auction. On Impression: fire when ad is served.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stats_api_link">Statistics API Link</Label>
                  <Input
                    id="stats_api_link"
                    value={formData.stats_api_link}
                    onChange={(e) => updateField("stats_api_link", e.target.value)}
                    placeholder="https://api.example.com/stats?date=[%Y-m-d%]"
                    data-testid="stats-api-input"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Schain Settings</CardTitle>
                <CardDescription>Configure supply chain settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <Label>Schain Type</Label>
                  <RadioGroup
                    value={formData.schain_type}
                    onValueChange={(v) => updateField("schain_type", v)}
                    className="grid grid-cols-2 md:grid-cols-3 gap-2"
                  >
                    {SCHAIN_TYPES.map((type) => (
                      <div key={type} className="flex items-center space-x-2">
                        <RadioGroupItem value={type} id={`schain-${type}`} />
                        <Label htmlFor={`schain-${type}`} className="cursor-pointer text-sm">
                          {type.replace(/_/g, " ")}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {formData.schain_type === "SSP_Schain" && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    <div className="space-y-2">
                      <Label>Schain Modification</Label>
                      <Select
                        value={formData.schain_modification || "none"}
                        onValueChange={(v) => updateField("schain_modification", v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="append">Append</SelectItem>
                          <SelectItem value="replace">Replace</SelectItem>
                          <SelectItem value="remove">Remove</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Complete Schain Status</Label>
                      <Select
                        value={formData.schain_complete_status || "none"}
                        onValueChange={(v) => updateField("schain_complete_status", v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="complete">Complete</SelectItem>
                          <SelectItem value="incomplete">Incomplete</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Schain Mode Limit</Label>
                      <Select
                        value={formData.schain_mode_limit || "none"}
                        onValueChange={(v) => updateField("schain_mode_limit", v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="first_hop">First Hop Only</SelectItem>
                          <SelectItem value="last_hop">Last Hop Only</SelectItem>
                          <SelectItem value="all_hops">All Hops</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label>Process Dual Schain Objects</Label>
                    <p className="text-sm text-muted-foreground">
                      Enable dual schain processing
                    </p>
                  </div>
                  <Switch
                    checked={formData.process_dual_schain}
                    onCheckedChange={(v) => updateField("process_dual_schain", v)}
                    data-testid="process-dual-schain-switch"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Targeting Tab */}
          <TabsContent value="targeting" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Ad Formats</CardTitle>
                <CardDescription>Select supported ad formats</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-end mb-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (formData.ad_formats.length === AD_FORMATS.length) {
                        updateField("ad_formats", []);
                      } else {
                        updateField("ad_formats", [...AD_FORMATS]);
                      }
                    }}
                    data-testid="select-all-ad-formats"
                  >
                    {formData.ad_formats.length === AD_FORMATS.length ? "Deselect All" : "Select All"}
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {AD_FORMATS.map((format) => (
                    <div key={format} className="flex items-center space-x-2">
                      <Checkbox
                        id={`format-${format}`}
                        checked={formData.ad_formats.includes(format)}
                        onCheckedChange={() => toggleArrayItem("ad_formats", format)}
                        data-testid={`ad-format-${format}`}
                      />
                      <Label htmlFor={`format-${format}`} className="cursor-pointer">
                        {format.replace("_", " ")}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Traffic Types</CardTitle>
                <CardDescription>Select target traffic sources</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-end mb-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (formData.traffic_types.length === TRAFFIC_TYPES.length) {
                        updateField("traffic_types", []);
                      } else {
                        updateField("traffic_types", [...TRAFFIC_TYPES]);
                      }
                    }}
                    data-testid="select-all-traffic-types"
                  >
                    {formData.traffic_types.length === TRAFFIC_TYPES.length ? "Deselect All" : "Select All"}
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {TRAFFIC_TYPES.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`traffic-${type}`}
                        checked={formData.traffic_types.includes(type)}
                        onCheckedChange={() => toggleArrayItem("traffic_types", type)}
                        data-testid={`traffic-type-${type}`}
                      />
                      <Label htmlFor={`traffic-${type}`} className="cursor-pointer">
                        {type.replace("_", " ")}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Filters Tab */}
          <TabsContent value="filters" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Country Targeting</CardTitle>
                <CardDescription>Allow or block specific countries</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={formData.filters.countries_mode}
                  onValueChange={(v) => updateFilter("countries_mode", v)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Allow" id="countries-allow" />
                    <Label htmlFor="countries-allow">Allow</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Block" id="countries-block" />
                    <Label htmlFor="countries-block">Block</Label>
                  </div>
                </RadioGroup>

                {/* Selected Countries */}
                {formData.filters.countries.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-2 border rounded-lg bg-muted/30">
                    {formData.filters.countries.map((code) => {
                      const country = countries.find((c) => c.code === code);
                      return (
                        <Badge key={code} variant="secondary" className="gap-1">
                          {country ? `${code} - ${country.name}` : code}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-destructive"
                            onClick={() => toggleFilterArrayItem("countries", code)}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                )}

                {/* Country Search & Selection */}
                <CountrySelector
                  countries={countries}
                  selectedCountries={formData.filters.countries}
                  onBulkSelect={handleCountryBulkSelect}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>SSP Targeting</CardTitle>
                <CardDescription>Allow or block specific SSPs</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={formData.filters.ssps_mode}
                  onValueChange={(v) => updateFilter("ssps_mode", v)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Allow" id="ssps-allow" />
                    <Label htmlFor="ssps-allow">Allow</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Block" id="ssps-block" />
                    <Label htmlFor="ssps-block">Block</Label>
                  </div>
                </RadioGroup>

                {ssps.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No SSPs available</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                    {ssps.map((ssp) => (
                      <div key={ssp.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`ssp-${ssp.id}`}
                          checked={formData.filters.ssps.includes(ssp.id)}
                          onCheckedChange={() => toggleFilterArrayItem("ssps", ssp.id)}
                        />
                        <Label htmlFor={`ssp-${ssp.id}`} className="cursor-pointer text-sm">
                          {ssp.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Device OS Targeting</CardTitle>
                <CardDescription>Allow or block specific operating systems</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={formData.filters.device_os_mode}
                  onValueChange={(v) => updateFilter("device_os_mode", v)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Allow" id="os-allow" />
                    <Label htmlFor="os-allow">Allow</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Block" id="os-block" />
                    <Label htmlFor="os-block">Block</Label>
                  </div>
                </RadioGroup>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {deviceOSList.map((os) => (
                    <div key={os.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`os-${os.id}`}
                        checked={formData.filters.device_os.includes(os.id)}
                        onCheckedChange={() => toggleFilterArrayItem("device_os", os.id)}
                      />
                      <Label htmlFor={`os-${os.id}`} className="cursor-pointer text-sm">
                        {os.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Ad Size Targeting */}
            <Card>
              <CardHeader>
                <CardTitle>Ad Size Targeting</CardTitle>
                <CardDescription>Allow or block specific ad sizes based on selected ad formats</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={formData.filters.banner_sizes_mode || "Allow"}
                  onValueChange={(v) => updateFilter("banner_sizes_mode", v)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Allow" id="bs-allow" />
                    <Label htmlFor="bs-allow">Allow</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Block" id="bs-block" />
                    <Label htmlFor="bs-block">Block</Label>
                  </div>
                </RadioGroup>

                <AdSizeSelector
                  selectedSizes={formData.filters.banner_sizes || []}
                  onChange={(sizes) => updateFilter("banner_sizes", sizes)}
                  adFormats={formData.ad_formats}
                />
              </CardContent>
            </Card>

            {/* Applied Filter Lists */}
            <Card>
              <CardHeader>
                <CardTitle>Filter Lists</CardTitle>
                <CardDescription>Apply allow/block lists to filter bid requests</CardDescription>
              </CardHeader>
              <CardContent>
                <ListSelector
                  api={api}
                  selected={formData.filters.filter_lists || []}
                  onChange={(lists) => updateFilter("filter_lists", lists)}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Advanced Tab */}
          <TabsContent value="advanced" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Advanced Filters</CardTitle>
                <CardDescription>Configure advanced filtering options</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: "mismatched_ip_filter", label: "Mismatched IP Filter" },
                    { key: "mismatched_bundle_filter", label: "Mismatched Bundle Filter" },
                    { key: "iab_ott_ctv_compliance", label: "IAB OTT/CTV Compliance" },
                    { key: "tcf", label: "TCF (Transparency & Consent)" },
                    { key: "ccpa_compliance", label: "CCPA Compliance" },
                    { key: "only_with_ifa", label: "Only with IFA" },
                    { key: "placement_required", label: "Placement Required" },
                    { key: "ipv4_required", label: "IPv4 Required" },
                    { key: "only_with_app_name", label: "Only with App Name" },
                    { key: "exclude_mraid_responses", label: "Exclude MRAID Responses" },
                    { key: "add_mraid_impression_pixel", label: "Add MRAID Impression Pixel" },
                    { key: "coppa_filtration", label: "COPPA Filtration" },
                    { key: "lmt_dnt_filter", label: "LMT & DNT Filter" },
                  ].map((filter) => (
                    <div
                      key={filter.key}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <Label htmlFor={filter.key}>{filter.label}</Label>
                      <Switch
                        id={filter.key}
                        checked={formData.advanced_filters[filter.key]}
                        onCheckedChange={(v) => updateAdvancedFilter(filter.key, v)}
                        data-testid={`advanced-filter-${filter.key}`}
                      />
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="badv_limit">BADV Limit</Label>
                    <Input
                      id="badv_limit"
                      type="text"
                      inputMode="numeric"
                      value={formData.advanced_filters.badv_limit}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*$/.test(val)) {
                          updateAdvancedFilter("badv_limit", val);
                        }
                      }}
                      placeholder="No limit"
                      data-testid="badv-limit-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="iab_subcategories_limit">IAB Sub-categories Limit</Label>
                    <Input
                      id="iab_subcategories_limit"
                      type="text"
                      inputMode="numeric"
                      value={formData.advanced_filters.iab_subcategories_limit}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*$/.test(val)) {
                          updateAdvancedFilter("iab_subcategories_limit", val);
                        }
                      }}
                      placeholder="No limit"
                      data-testid="iab-subcategories-limit-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label>Secure Mode</Label>
                    <Select
                      value={formData.advanced_filters.secure_mode}
                      onValueChange={(v) => updateAdvancedFilter("secure_mode", v)}
                    >
                      <SelectTrigger data-testid="secure-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SECURE_MODES.map((mode) => (
                          <SelectItem key={mode} value={mode}>
                            {mode.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Adult Filter</Label>
                    <Select
                      value={formData.advanced_filters.adult_filter || "allow_all"}
                      onValueChange={(v) => updateAdvancedFilter("adult_filter", v)}
                    >
                      <SelectTrigger data-testid="adult-filter-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="allow_all">Allow All Traffic</SelectItem>
                        <SelectItem value="adult_only">Adult Only</SelectItem>
                        <SelectItem value="exclude_adult">Exclude Adult</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Cellular Connection Type</Label>
                    <Select
                      value={formData.advanced_filters.cellular_connection_type || "All"}
                      onValueChange={(v) => updateAdvancedFilter("cellular_connection_type", v)}
                    >
                      <SelectTrigger data-testid="cellular-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="All">All</SelectItem>
                        <SelectItem value="2G">2G</SelectItem>
                        <SelectItem value="3G">3G</SelectItem>
                        <SelectItem value="4G">4G / LTE</SelectItem>
                        <SelectItem value="5G">5G</SelectItem>
                        <SelectItem value="WiFi">WiFi Only</SelectItem>
                        <SelectItem value="Ethernet">Ethernet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Additional Settings</CardTitle>
                <CardDescription>Configure additional DSP options</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { key: "pass_omsdk", label: "Pass OMSDK" },
                    { key: "pass_display_manager", label: "Pass Display Manager" },
                    { key: "pass_pchain", label: "Pass Pchain" },
                    { key: "send_gzip_requests", label: "Send GZip requests" },
                  ].map((setting) => (
                    <div
                      key={setting.key}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <Label htmlFor={setting.key}>{setting.label}</Label>
                      <Switch
                        id={setting.key}
                        checked={formData.settings[setting.key]}
                        onCheckedChange={(v) => updateSetting(setting.key, v)}
                        data-testid={`setting-${setting.key}`}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4 mt-6 pt-6 border-t">
          <Button type="button" variant="outline" onClick={() => navigate("/dsps")}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} data-testid="save-dsp-btn">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEdit ? "Update DSP" : "Create DSP"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
